import { useEffect, useMemo } from 'react';
import { useLocation, useSearchParams, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useSelection } from '../../context/SelectionContext';
import { useChildren } from '../../hooks/useChildren';
import { useChildrenIncompleteCounts, useChildrenUnviewedCounts } from '../../hooks/useChildrenCounts';
import { useDragScroll } from '../../hooks/useDragScroll';
import NotificationBadge from '../common/NotificationBadge';

const EMPTY_ARRAY = [];

export default function ChildrenSelector() {
  const { t } = useTranslation();
  const location = useLocation();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { children, loading, error } = useChildren();
  const { selectedChild, selectChild } = useSelection();
  const { ref: dragScrollRef } = useDragScroll();

  // Determine which page we're on
  const isResearchPage = location.pathname.startsWith('/research');
  const isPlannerPage = location.pathname.startsWith('/planner');

  // Fetch counts only for relevant pages
  const childrenForResearchCounts = useMemo(() => isResearchPage ? children : EMPTY_ARRAY, [isResearchPage, children]);
  const childrenForPlannerCounts = useMemo(() => isPlannerPage ? children : EMPTY_ARRAY, [isPlannerPage, children]);

  const { counts: incompleteCounts } = useChildrenIncompleteCounts(childrenForResearchCounts);
  const { counts: unviewedCounts } = useChildrenUnviewedCounts(childrenForPlannerCounts);

  // Auto-select child on load - prioritize URL param, then stored, then first
  useEffect(() => {
    if (children.length > 0) {
      // Check for student UUID in URL query param (from auto-login redirect)
      const studentUuid = searchParams.get('student');
      if (studentUuid) {
        const targetChild = children.find(c => c.uuid === studentUuid);
        if (targetChild) {
          selectChild(targetChild);
          // Clean up URL by removing only the student param (preserve course param for CourseSelector)
          const newParams = new URLSearchParams(searchParams);
          newParams.delete('student');
          const newSearch = newParams.toString();
          navigate(`${location.pathname}${newSearch ? `?${newSearch}` : ''}`, { replace: true });
          return;
        }
      }

      if (!selectedChild) {
        // No child selected, select first
        selectChild(children[0]);
      } else {
        // Validate that stored child belongs to this parent
        const isValidChild = children.some(c => c.id === selectedChild.id);
        if (!isValidChild) {
          // Stored child doesn't belong to this parent, reset to first
          selectChild(children[0]);
        }
      }
    }
  }, [children, selectedChild, selectChild, searchParams, navigate, location.pathname]);

  if (loading) {
    return (
      <div className="px-4 py-2.5 sm:px-6 sm:py-3 md:px-12 lg:px-20 border-b border-[#E5E5E5] dark:border-gray-700">
        <div className="flex items-center gap-2 sm:gap-3 h-[28px] sm:h-[36px]">
          <span className="text-[10px] sm:text-xs font-medium text-gray-400 dark:text-gray-500 uppercase tracking-wider flex-shrink-0 w-12 sm:w-16 text-center">{t('selector.student')}</span>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="px-4 py-2.5 sm:px-6 sm:py-3 md:px-12 lg:px-20 border-b border-[#E5E5E5] dark:border-gray-700">
        <div className="flex items-center gap-2 sm:gap-3 h-[28px] sm:h-[36px]">
          <span className="text-[10px] sm:text-xs font-medium text-gray-400 dark:text-gray-500 uppercase tracking-wider flex-shrink-0 w-12 sm:w-16 text-center">{t('selector.student')}</span>
          <span className="text-red-600 dark:text-red-400 text-xs sm:text-sm">{error}</span>
        </div>
      </div>
    );
  }

  if (children.length === 0) {
    return (
      <div className="px-4 py-2.5 sm:px-6 sm:py-3 md:px-12 lg:px-20 border-b border-[#E5E5E5] dark:border-gray-700">
        <div className="flex items-center gap-2 sm:gap-3 h-[28px] sm:h-[36px]">
          <span className="text-[10px] sm:text-xs font-medium text-gray-400 dark:text-gray-500 uppercase tracking-wider flex-shrink-0 w-12 sm:w-16 text-center">{t('selector.student')}</span>
          <span className="text-gray-500 dark:text-gray-400 text-xs sm:text-sm">{t('selector.noStudents')}</span>
        </div>
      </div>
    );
  }

  const getChildDisplayName = (child) => {
    const parts = [];
    if (child.first_name) parts.push(child.first_name);
    if (child.korean_name) parts.push(child.korean_name);
    if (child.last_name) parts.push(child.last_name);
    const name = parts.join(' ') || `Student ${child.id}`;
    return child.consultation_number ? `${name} (${child.consultation_number})` : name;
  };

  return (
    <div ref={dragScrollRef} className="px-4 py-2.5 sm:px-6 sm:py-3 md:px-12 lg:px-20 overflow-x-auto border-b border-[#E5E5E5] dark:border-gray-700 scrollbar-hide">
      <div className="flex items-center gap-2 sm:gap-3 h-[28px] sm:h-[36px]">
        <span className="text-[10px] sm:text-xs font-medium text-gray-400 dark:text-gray-500 uppercase tracking-wider flex-shrink-0 w-12 sm:w-16 text-center">{t('selector.student')}</span>
        <div className="flex gap-1.5 sm:gap-2">
          {children.map((child) => (
            <button
              key={child.id}
              onClick={() => selectChild(child)}
              className={`relative px-3 py-1.5 sm:px-4 sm:py-2 rounded-lg text-xs sm:text-sm font-medium whitespace-nowrap transition-all duration-200 ${
                selectedChild?.id === child.id
                  ? 'bg-[#589DD5] text-white shadow-sm'
                  : 'bg-white dark:bg-gray-800 text-gray-600 dark:text-gray-300 border border-gray-200 dark:border-gray-600 hover:border-[#589DD5] hover:text-[#589DD5]'
              }`}
            >
              {getChildDisplayName(child)}
              {isResearchPage && (
                <NotificationBadge
                  count={incompleteCounts[child.id]}
                  size="xs"
                  className="absolute -top-1.5 -right-1.5"
                />
              )}
              {isPlannerPage && (
                <NotificationBadge
                  count={unviewedCounts[child.id]}
                  size="xs"
                  className="absolute -top-1.5 -right-1.5"
                />
              )}
            </button>
          ))}
        </div>
      </div>
    </div>
  );
}
